import { useState } from 'react';
import { Navigate, useParams, useNavigate } from 'react-router-dom';
import { Spin, Tabs } from 'antd';
import { ClienteDetailHeader } from './components/ClienteDetailHeader';
import { ClienteKPIs } from './components/ClienteKPIs';
import { ClienteInfoTab } from './components/ClienteInfoTab';
import { ClienteServiciosList } from './components/ClienteServiciosList';
import { ClienteFacturasList } from './components/ClienteFacturasList';
import { ClientePagosList } from './components/ClientePagosList';
import { ClienteVisitasList } from './components/ClienteVisitasList';
import { ClienteExcepcionesList } from './components/ClienteExcepcionesList';
import { ClienteEstadoCuentaSimple } from './components/ClienteEstadoCuentaSimple';
import { ClientDeactivationModal } from './components/ClientDeactivationModal';
import { ClienteEditOverlay } from './ClienteEditOverlay';
import { useCliente } from './api/useClientes';
import { useHasPermission } from '../../auth/useHasPermission';
import { useT } from '../../locales';
import { EmptyState } from '../../components/EmptyState';
import { Permission } from '@aid/shared';
import { CLIENTE_TABS, resolveClienteTab } from './cliente-tabs';
import type { ClienteTab } from './cliente-tabs';

export interface ClienteDetailPageProps {
  /**
   * Deep-link mode: the /clientes/:id/edit route renders and owns the edit
   * overlay (always open). The page then skips its in-memory overlay and the
   * header edit action becomes a no-op (the overlay mask already blocks it).
   */
  editOverlayManagedExternally?: boolean;
}

export function ClienteDetailPage({
  editOverlayManagedExternally = false,
}: ClienteDetailPageProps = {}) {
  const t = useT();
  const { id, tab: tabSegment } = useParams<{ id: string; tab?: string }>();
  const navigate = useNavigate();
  const clienteId = Number(id);
  const { data: cliente, isLoading, error } = useCliente(clienteId);
  const canUpdate = useHasPermission(Permission.CLIENTES_UPDATE);
  const canReadCobros = useHasPermission(Permission.COBROS_READ);
  const canReadEstadoCuenta = useHasPermission(Permission.ESTADO_CUENTA_READ);
  const canReadPagos = useHasPermission(Permission.PAGOS_READ);
  const canReadVisitas = useHasPermission(Permission.VISITAS_READ);

  const [deactivationModalOpen, setDeactivationModalOpen] = useState(false);
  // The edit overlay is in-memory state: opening it never navigates, so the
  // active tab URL (/clientes/:id/:tab) keeps rendering underneath and
  // closing returns to exactly the same route.
  const [editOverlayOpen, setEditOverlayOpen] = useState(false);

  // The selected tab lives in the URL (/clientes/:id/:tab). Unknown or
  // permission-blocked segments normalize back to the client root so a bad
  // link never renders a blank page.
  const { tab, needsRedirect } = resolveClienteTab(
    tabSegment,
    canReadCobros,
    canReadEstadoCuenta,
    canReadPagos,
    canReadVisitas,
  );

  if (needsRedirect) {
    return <Navigate to={`/clientes/${clienteId}`} replace />;
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !cliente) {
    return (
      <EmptyState
        title={t('pages.clientes.notFound')}
        description={t('pages.clientes.notFoundDetail')}
      />
    );
  }

  const handleTabChange = (key: string) => {
    const next = key as ClienteTab;
    // info owns the bare client root; the rest append their segment.
    navigate(
      next === CLIENTE_TABS.INFO ? `/clientes/${cliente.id}` : `/clientes/${cliente.id}/${next}`,
    );
  };

  const tabs = [
    {
      key: CLIENTE_TABS.INFO,
      label: t('pages.clientes.tabs.info'),
      children: <ClienteInfoTab cliente={cliente} />,
    },
    {
      key: CLIENTE_TABS.SERVICIOS,
      label: t('pages.clientes.tabs.servicios'),
      children: <ClienteServiciosList clienteId={cliente.id} />,
    },
    ...(canReadCobros
      ? [
          {
            key: CLIENTE_TABS.FACTURAS,
            label: t('pages.clientes.tabs.facturas'),
            children: <ClienteFacturasList clienteId={cliente.id} />,
          },
        ]
      : []),
    ...(canReadPagos
      ? [
          {
            key: CLIENTE_TABS.PAGOS,
            label: t('pages.clientes.tabs.pagos'),
            children: <ClientePagosList clienteId={cliente.id} />,
          },
        ]
      : []),
    ...(canReadEstadoCuenta
      ? [
          {
            key: CLIENTE_TABS.ESTADO_CUENTA,
            label: t('pages.clientes.tabs.estadoCuenta'),
            children: <ClienteEstadoCuentaSimple clienteId={cliente.id} />,
          },
        ]
      : []),
    ...(canReadVisitas
      ? [
          {
            key: CLIENTE_TABS.VISITAS,
            label: t('pages.clientes.tabs.visitas'),
            children: <ClienteVisitasList clienteId={cliente.id} />,
          },
        ]
      : []),
    {
      key: CLIENTE_TABS.EXCEPCIONES,
      label: t('pages.clientes.tabs.excepciones'),
      children: <ClienteExcepcionesList clienteId={cliente.id} />,
    },
  ];

  return (
    <div>
      <ClienteDetailHeader
        cliente={cliente}
        canUpdate={canUpdate}
        canDeactivate={
          canUpdate &&
          cliente.estadoCliente?.key !== 'INACTIVO' &&
          cliente.estadoCliente?.key !== 'BAJA'
        }
        onEdit={editOverlayManagedExternally ? () => undefined : () => setEditOverlayOpen(true)}
        onDeactivate={() => setDeactivationModalOpen(true)}
      />
      <ClienteKPIs clienteId={cliente.id} />
      <Tabs items={tabs} activeKey={tab} onChange={handleTabChange} destroyOnHidden />
      <ClientDeactivationModal
        open={deactivationModalOpen}
        cliente={cliente}
        onClose={() => setDeactivationModalOpen(false)}
      />
      {!editOverlayManagedExternally && (
        <ClienteEditOverlay
          clienteId={cliente.id}
          open={editOverlayOpen}
          onClose={() => setEditOverlayOpen(false)}
        />
      )}
    </div>
  );
}
